<template>
  <div class="summary-card">
    <div class="summary-top">
      <span class="savings-tile">
        <InvIcon name="savings" />
      </span>
      <span class="summary-label">Portfolio Summary</span>

      <div class="currency-select">
        <button class="currency-trigger" type="button" @click="open = !open">
          {{ currency }}
          <InvIcon name="chevron" class="chevron" />
        </button>
        <template v-if="open">
          <div class="currency-backdrop" @click="open = false"></div>
          <ul class="currency-menu">
            <li
              v-for="c in currencies"
              :key="c"
              :class="{ active: c === currency }"
              @click="select(c)"
            >
              <span class="cur-left">
                <span class="cur-sym">{{ symbol(c) }}</span>
                <span class="cur-code">{{ c }}</span>
              </span>
              <InvIcon v-if="c === currency" name="check" class="cur-check" />
            </li>
          </ul>
        </template>
      </div>
    </div>

    <div class="summary-stats">
      <div class="stat">
        <p class="stat-label">Total {{ currency }} Shares</p>
        <p class="stat-value">{{ sharesLabel }}</p>
      </div>
      <div class="divider"></div>
      <div class="stat">
        <p class="stat-label">Cash Value ({{ currency }})</p>
        <p class="stat-value">{{ cashLabel }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import InvIcon from "@/components/invest/InvIcon.vue";
import * as inv from "@/utils/invest.js";

export default {
  name: "InvestSummaryCard",
  components: { InvIcon },
  props: {
    currency: { type: String, required: true },
    currencies: { type: Array, default: () => inv.INVEST_CURRENCIES },
    totalShares: { type: Number, default: 0 },
    cashValue: { type: Number, default: 0 },
  },
  emits: ["change-currency"],
  data() {
    return { open: false };
  },
  computed: {
    sharesLabel() {
      return inv.shareCount(this.totalShares);
    },
    cashLabel() {
      return inv.money(this.cashValue, this.currency, 2);
    },
  },
  methods: {
    symbol: inv.currencySymbol,
    select(c) {
      this.open = false;
      if (c !== this.currency) this.$emit("change-currency", c);
    },
  },
};
</script>

<style lang="scss" scoped>
.summary-card {
  position: relative;
  // The currency menu opens downward into the section below, which is later in the DOM and
  // full of positioned chart elements. Without this they paint over it at equal stacking.
  // It lives here rather than on the parent so the fix travels with the component.
  z-index: 2;
  border-radius: 8px;
  padding: 20px;
  color: #ffffff;
  background: linear-gradient(135deg, #001140 0%, #06206b 100%);

  @media (min-width: 1024px) {
    padding: 28px 32px;
  }
}

.summary-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 22px;

  @media (min-width: 1024px) {
    margin-bottom: 26px;
  }
}

.savings-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 18px;
  color: #ffffff;
}

.summary-label {
  flex: 1;
  font-size: 11px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
}

/* Currency dropdown */
.currency-select {
  position: relative;
  flex-shrink: 0;
}

.currency-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  .chevron {
    font-size: 16px;
    opacity: 0.8;
  }
}

.currency-backdrop {
  position: fixed;
  inset: 0;
  z-index: 20;
}

.currency-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 21;
  min-width: 160px;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 12px;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background: #f7f9fd;
    }
    &.active {
      background: rgba(78, 132, 255, 0.1);
    }
  }

  .cur-left {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
  .cur-sym {
    font-size: 13px;
    font-weight: 500;
    color: #001240;
  }
  .cur-code {
    font-size: 12px;
    color: #4f5877;
  }
  .cur-check {
    font-size: 16px;
    color: #4e84ff;
  }
}

/* Stats — grouped left so the full-width banner doesn't feel empty */
.summary-stats {
  display: flex;
  align-items: stretch;
  gap: 20px;
  max-width: 560px;

  @media (min-width: 1024px) {
    gap: 56px;
    max-width: none;
  }
}

.stat {
  flex: 1;
  min-width: 0;

  @media (min-width: 1024px) {
    flex: 0 1 auto;
    min-width: 220px;
  }
}

.stat-label {
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.6);

  @media (min-width: 1024px) {
    font-size: 12px;
    margin-bottom: 8px;
  }
}

.stat-value {
  margin: 0;
  font-size: 19px;
  font-weight: 500;
  color: #ffffff;
  overflow-wrap: anywhere;

  @media (min-width: 1024px) {
    font-size: 28px;
  }
}

.divider {
  width: 1px;
  background: rgba(255, 255, 255, 0.15);
}
</style>
